import { Box, Flex, Icon, InlineNotification, TableIconButton, Td, Text, Tooltip, Tr, useTranslation } from "@familyzone/component-library";
import React from "react";
import CreateTemporarySession from "../../../modules/CreateTemporarySession";
import { DeleteButtonNew } from "../../../modules/DeleteButtonNew";
import { useSessionStore } from "../../../storez/SessionStore";
import { UnifiedUserUMS } from "../../../types/Users";
import { PurgeType } from "../../../utils/api/Helpers";
import Link from "../../Link";
import TableBasedPage from "../../templates/TableBasedPage";
import AddUser from "../Users/AddUser";
import PurgeUsers from "../Users/PurgeUsers";

interface UnifiedUsersProps {
  users: UnifiedUserUMS[];
  loaded: boolean;
  onDeleteUser: (id: string) => void;
  withinTableChildren: JSX.Element;
  isSearching?: boolean;
  onPurgeUsers: (usersToPurge: PurgeType) => void;
}

const UnifiedUsersNew: React.FC<UnifiedUsersProps> = ({
  users,
  loaded,
  withinTableChildren,
  onDeleteUser,
  isSearching = false,
  onPurgeUsers,
}) => {
  const { t } = useTranslation();
  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Users and Groups"), url: "/config/device/userdb", isActive: false },
    { title: t("Users"), isActive: true },
  ];

  const columns = [
    { headerText: t("Username"), columnName: "username", sortable: true, searchable: true },
    { headerText: t("Email"), columnName: "email", sortable: true, searchable: true },
    { headerText: t("First Name"), columnName: "firstName", sortable: true, searchable: true },
    { headerText: t("Last Name"), columnName: "lastName", sortable: true, searchable: true },
    { headerText: t("Operations"), columnName: "operations", sortable: false, searchable: false },
  ];

  const operations = (username: string, id: string, managedById: string, isLocal: boolean): JSX.Element => {
    return (
      <Flex>
        <Tooltip label={t("View")} hasArrow variant={"dark"}>
          <Box mr="sp8">
            <Link to={`/config/device/userdb/users/qid/${id}`}>
              <TableIconButton
                data-testid="users-view-button"
                aria-label="View"
                icon={<Icon icon="fa-eye" variant="solid" color="text.paragraph.light" />}
              />
            </Link>
          </Box>
        </Tooltip>

        {isLocal && (
          <Tooltip label={t("Delete")} hasArrow variant={"dark"}>
            <Box mr="sp8">
              <DeleteButtonNew onClick={() => onDeleteUser(managedById)} titleToDelete="User" bodyToDelete="user" />
            </Box>
          </Tooltip>
        )}
        {isLocal && (
          <Tooltip label={t("Create Temporary Session")} hasArrow variant={"dark"}>
            <Box mr="sp8">
              <CreateTemporarySession username={username} />
            </Box>
          </Tooltip>
        )}
      </Flex>
    );
  };

  const tableDataMap = (user: UnifiedUserUMS, index: number): JSX.Element => {
    return (
      <Tr key={index}>
        <Td data-testid="user-username">
          <Link to={`/config/device/userdb/users/qid/${user.id}`}>
            <Text fontSize={"sm"}>{user.username}</Text>
          </Link>
        </Td>
        <Td data-testid="user-email">
          <Text>{user.email}</Text>
        </Td>
        <Td data-testid="user-first-name">
          <Text>{user.firstName}</Text>
        </Td>
        <Td data-testid="user-last-name">
          <Text>{user.lastName}</Text>
        </Td>
        <Td>{operations(user.username, user.id, user.primarySourceUserID, user.isLocal)}</Td>
      </Tr>
    );
  };

  const aboveTableChildren = (): JSX.Element => {
    return (
      <Flex mt="sp24" mr="sp24" justifyContent="end">
        <Box mr="sp8">
          <AddUser qUser={true} />
        </Box>
        {isSupportAdmin && <PurgeUsers onPurgeUsers={onPurgeUsers} />}
      </Flex>
    );
  };

  const moreResultsNotification = (): JSX.Element => {
    if (!loaded || isSearching) {
      return <></>;
    }
    return (
      <Box ml="sp12" minW="300px" maxH="sp12">
        <InlineNotification
          notificationDescription="Please use search to display more users."
          notificationTitle={`Showing you ${users.length} user(s)`}
          status="info"
        />
      </Box>
    );
  };

  const tableChildren = (): JSX.Element => {
    return (
      <Flex>
        {withinTableChildren}
        {moreResultsNotification()}
      </Flex>
    );
  };

  return (
    <TableBasedPage
      title="Users"
      loaded={loaded}
      breadcrumbs={breadcrumbs}
      columns={columns}
      data={users}
      tableDataMap={tableDataMap}
      children={aboveTableChildren()}
      childrenInTableHeader={tableChildren()}
      showSearch={false}
    />
  );
};

export default UnifiedUsersNew;
